import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  GlobalStyles,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BrushIcon from "@mui/icons-material/Brush";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ChairIcon from "@mui/icons-material/Chair";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EmailIcon from "@mui/icons-material/Email";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PaletteIcon from "@mui/icons-material/Palette";
import PaymentIcon from "@mui/icons-material/Payment";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import StraightenIcon from "@mui/icons-material/Straighten";
import TuneIcon from "@mui/icons-material/Tune";
import ViewInArIcon from "@mui/icons-material/ViewInAr";

const ASSETS = {
  logo: "/logo.png",
  video: "/video.mp4",
  video1: "/video1.mp4",
  video2: "/video2.mp4",
  southBankInnovation: "/SouthBankInnovation.png",
};

const SITE_NAME = "Art For My Room";
const DOMAIN = "ArtForMyRoom.com";
const CONTACT_EMAIL = "info@artformyroom.com";

const NOIR = "#050707";
const INK = "#081211";
const INK_SOFT = "#14201F";
const VELVET = "#240714";
const BURGUNDY = "#7A1232";
const BURGUNDY_DEEP = "#4A071D";
const BURGUNDY_MIST = "#F8E9EE";
const TURQUOISE = "#00B4A6";
const TURQUOISE_DEEP = "#006F66";
const TURQUOISE_MIST = "#DDFBF8";
const GOLD = "#D4AF37";
const GOLD_DEEP = "#8A6500";
const GOLD_MIST = "#FFF2C8";
const CHAMPAGNE = "#F6E7C7";
const IVORY = "#FBF7EF";
const WHITE = "#FFFFFF";
const MUTED = "#786F64";
const LINE_DARK = "rgba(246, 231, 199, 0.18)";
const LINE_LIGHT = "rgba(8, 18, 17, 0.10)";

const DISPLAY_FONT =
  "'Cormorant Garamond', 'Playfair Display', Georgia, 'Times New Roman', serif";

const HEADING_FONT =
  "'Space Grotesk', Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const BODY_FONT =
  "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const MONO_FONT =
  "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace";

const Page = styled(Box)({
  minHeight: "100vh",
  color: INK,
  fontFamily: BODY_FONT,
  background:
    `radial-gradient(circle at 7% 2%, ${alpha(TURQUOISE, 0.14)}, transparent 28%),
     radial-gradient(circle at 92% 7%, ${alpha(GOLD, 0.18)}, transparent 26%),
     radial-gradient(circle at 88% 72%, ${alpha(BURGUNDY, 0.12)}, transparent 30%),
     linear-gradient(180deg, #fffaf1 0%, ${IVORY} 44%, #ffffff 100%)`,
});

const SalonPanel = styled(Paper)({
  position: "relative",
  overflow: "hidden",
  borderRadius: 38,
  color: WHITE,
  background:
    `radial-gradient(circle at 12% 12%, ${alpha(TURQUOISE, 0.22)}, transparent 30%),
     radial-gradient(circle at 88% 10%, ${alpha(GOLD, 0.20)}, transparent 29%),
     radial-gradient(circle at 88% 78%, ${alpha(BURGUNDY, 0.40)}, transparent 34%),
     linear-gradient(135deg, ${NOIR} 0%, ${INK_SOFT} 52%, ${VELVET} 100%)`,
  border: `1px solid ${LINE_DARK}`,
  boxShadow: "0 44px 140px rgba(5, 7, 7, 0.36)",
  "&:before": {
    content: '""',
    position: "absolute",
    inset: 18,
    borderRadius: 30,
    border: `1px solid ${alpha(CHAMPAGNE, 0.22)}`,
    pointerEvents: "none",
  },
});

const IvoryPanel = styled(Paper)({
  borderRadius: 34,
  background:
    `linear-gradient(180deg, ${alpha(WHITE, 0.94)} 0%, ${alpha(IVORY, 0.92)} 100%)`,
  border: `1px solid ${LINE_LIGHT}`,
  boxShadow: "0 24px 80px rgba(8, 18, 17, 0.08)",
  overflow: "hidden",
});

const EditorialCard = styled(Card)({
  height: "100%",
  borderRadius: 30,
  background:
    `linear-gradient(180deg, ${WHITE} 0%, ${alpha(IVORY, 0.78)} 100%)`,
  border: `1px solid ${LINE_LIGHT}`,
  boxShadow: "0 18px 54px rgba(8, 18, 17, 0.07)",
  transition: "transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 26px 76px rgba(8, 18, 17, 0.12)",
    borderColor: alpha(GOLD, 0.42),
  },
});

const LuxuryButton = styled(Button)({
  borderRadius: 999,
  padding: "14px 24px",
  fontWeight: 950,
  textTransform: "none",
  letterSpacing: "0.01em",
  fontFamily: BODY_FONT,
  color: INK,
  background:
    `linear-gradient(135deg, ${GOLD_MIST} 0%, ${GOLD} 48%, #B88A18 100%)`,
  boxShadow: `0 18px 42px ${alpha(GOLD, 0.24)}`,
  border: `1px solid ${alpha(CHAMPAGNE, 0.55)}`,
  "&:hover": {
    background:
      `linear-gradient(135deg, ${GOLD} 0%, #B88A18 55%, ${GOLD_DEEP} 100%)`,
    transform: "translateY(-1px)",
    boxShadow: `0 22px 54px ${alpha(GOLD, 0.32)}`,
  },
  "&.Mui-disabled": {
    color: alpha(INK, 0.62),
    background: alpha(GOLD, 0.42),
  },
});

const VelvetButton = styled(Button)({
  borderRadius: 999,
  padding: "14px 24px",
  fontWeight: 950,
  textTransform: "none",
  fontFamily: BODY_FONT,
  color: WHITE,
  background:
    `linear-gradient(135deg, ${BURGUNDY} 0%, ${BURGUNDY_DEEP} 100%)`,
  boxShadow: `0 18px 42px ${alpha(BURGUNDY, 0.20)}`,
  border: `1px solid ${alpha(BURGUNDY_MIST, 0.32)}`,
  "&:hover": {
    background:
      `linear-gradient(135deg, ${BURGUNDY_DEEP} 0%, ${BURGUNDY} 100%)`,
    transform: "translateY(-1px)",
  },
});

const GhostButton = styled(Button)({
  borderRadius: 999,
  padding: "13px 22px",
  fontWeight: 950,
  textTransform: "none",
  fontFamily: BODY_FONT,
  color: INK,
  border: `1px solid ${alpha(INK, 0.16)}`,
  background: alpha(WHITE, 0.72),
  "&:hover": {
    background: WHITE,
    borderColor: alpha(GOLD, 0.46),
  },
});

const DarkGhostButton = styled(Button)({
  borderRadius: 999,
  padding: "13px 22px",
  fontWeight: 950,
  textTransform: "none",
  fontFamily: BODY_FONT,
  color: WHITE,
  border: `1px solid ${alpha(CHAMPAGNE, 0.30)}`,
  background: alpha(WHITE, 0.08),
  "&:hover": {
    background: alpha(WHITE, 0.14),
    borderColor: alpha(CHAMPAGNE, 0.45),
  },
});

const Eyebrow = styled(Chip)({
  height: "auto",
  fontFamily: MONO_FONT,
  fontWeight: 950,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  background: alpha(GOLD_MIST, 0.88),
  color: GOLD_DEEP,
  border: `1px solid ${alpha(GOLD, 0.25)}`,
  "& .MuiChip-icon": {
    color: GOLD_DEEP,
  },
  "& .MuiChip-label": {
    paddingTop: 7,
    paddingBottom: 7,
    whiteSpace: "normal",
  },
});

const SectionTitle = ({ eyebrow, icon, title, text, align = "center", light = false }) => (
  <Box sx={{ textAlign: align, mb: { xs: 3.5, md: 5 } }}>
    {eyebrow && (
      <Eyebrow
        icon={icon}
        label={eyebrow}
        sx={{
          mb: 2,
          bgcolor: light ? alpha(WHITE, 0.10) : alpha(GOLD_MIST, 0.92),
          color: light ? CHAMPAGNE : GOLD_DEEP,
          borderColor: light ? alpha(CHAMPAGNE, 0.22) : alpha(GOLD, 0.25),
          "& .MuiChip-icon": { color: light ? GOLD : GOLD_DEEP },
        }}
      />
    )}

    <Typography
      component="h2"
      sx={{
        fontFamily: DISPLAY_FONT,
        fontWeight: 700,
        fontSize: { xs: "2.45rem", md: "4.6rem" },
        lineHeight: 0.94,
        letterSpacing: "-0.055em",
        color: light ? WHITE : INK,
        maxWidth: align === "center" ? 940 : "100%",
        mx: align === "center" ? "auto" : 0,
      }}
    >
      {title}
    </Typography>

    {text && (
      <Typography
        sx={{
          mt: 1.8,
          color: light ? alpha(WHITE, 0.72) : MUTED,
          lineHeight: 1.85,
          fontSize: { xs: "1rem", md: "1.1rem" },
          maxWidth: align === "center" ? 790 : 700,
          mx: align === "center" ? "auto" : 0,
        }}
      >
        {text}
      </Typography>
    )}
  </Box>
);

const ImageLogo = ({ sx }) => (
  <Box
    component="img"
    src={ASSETS.logo}
    alt="Art For My Room logo"
    loading="lazy"
    decoding="async"
    sx={{
      width: 58,
      height: 58,
      borderRadius: "50%",
      objectFit: "contain",
      background: alpha(WHITE, 0.94),
      p: 0.65,
      border: `1px solid ${alpha(GOLD, 0.34)}`,
      boxShadow: `0 12px 34px ${alpha(GOLD, 0.18)}`,
      ...sx,
    }}
  />
);

const IconMedallion = ({ children, tone = "gold" }) => {
  const palette = {
    gold: { bg: GOLD_MIST, fg: GOLD_DEEP },
    turquoise: { bg: TURQUOISE_MIST, fg: TURQUOISE_DEEP },
    burgundy: { bg: BURGUNDY_MIST, fg: BURGUNDY },
    dark: { bg: alpha(WHITE, 0.10), fg: CHAMPAGNE },
  }[tone];

  return (
    <Box
      sx={{
        width: 62,
        height: 62,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        bgcolor: palette.bg,
        color: palette.fg,
        border: `1px solid ${alpha(palette.fg, 0.16)}`,
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
};

const Metric = ({ value, label }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1.6, md: 2 },
      borderRadius: 6,
      bgcolor: alpha(WHITE, 0.08),
      border: `1px solid ${alpha(CHAMPAGNE, 0.18)}`,
      color: WHITE,
      backdropFilter: "blur(14px)",
    }}
  >
    <Typography
      sx={{
        fontFamily: DISPLAY_FONT,
        fontWeight: 700,
        fontSize: { xs: "1.65rem", md: "2.05rem" },
        lineHeight: 1,
        letterSpacing: "-0.04em",
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        mt: 0.6,
        color: alpha(WHITE, 0.66),
        fontWeight: 850,
        fontSize: "0.88rem",
      }}
    >
      {label}
    </Typography>
  </Paper>
);

const VideoFrame = ({ src, label, title, text, large = false }) => (
  <Paper
    elevation={0}
    sx={{
      position: "relative",
      overflow: "hidden",
      height: "100%",
      borderRadius: large ? 34 : 30,
      bgcolor: large ? alpha(WHITE, 0.10) : WHITE,
      border: large ? `1px solid ${alpha(CHAMPAGNE, 0.20)}` : `1px solid ${LINE_LIGHT}`,
      boxShadow: large
        ? "0 34px 100px rgba(0,0,0,0.32)"
        : "0 20px 60px rgba(8, 18, 17, 0.09)",
    }}
  >
    <Box
      sx={{
        p: large ? { xs: 1.2, md: 1.6 } : 1.1,
        bgcolor: large ? alpha(NOIR, 0.32) : alpha(IVORY, 0.72),
      }}
    >
      <Box
        component="video"
        src={src}
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
        controls={large}
        sx={{
          display: "block",
          width: "100%",
          aspectRatio: large ? "16 / 9" : "4 / 5",
          objectFit: "cover",
          background: INK,
          borderRadius: large ? 26 : 24,
          border: large
            ? `1px solid ${alpha(CHAMPAGNE, 0.18)}`
            : `1px solid ${alpha(INK, 0.08)}`,
        }}
      />
    </Box>

    <Box sx={{ p: { xs: 2.2, md: large ? 3 : 2.6 } }}>
      {label && (
        <Chip
          size="small"
          label={label}
          sx={{
            mb: 1.3,
            bgcolor: large ? alpha(GOLD, 0.16) : BURGUNDY_MIST,
            color: large ? CHAMPAGNE : BURGUNDY,
            border: large ? `1px solid ${alpha(CHAMPAGNE, 0.18)}` : "none",
            fontWeight: 950,
          }}
        />
      )}

      <Typography
        sx={{
          fontFamily: DISPLAY_FONT,
          fontWeight: 700,
          fontSize: large ? { xs: "1.7rem", md: "2.3rem" } : "1.55rem",
          letterSpacing: "-0.045em",
          lineHeight: 1,
          color: large ? WHITE : INK,
        }}
      >
        {title}
      </Typography>

      {text && (
        <Typography
          sx={{
            mt: 1,
            color: large ? alpha(WHITE, 0.70) : MUTED,
            lineHeight: 1.7,
            fontSize: large ? "1rem" : "0.96rem",
          }}
        >
          {text}
        </Typography>
      )}
    </Box>
  </Paper>
);

const LuxuryDivider = ({ light = false }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 2 }}>
    <Box
      sx={{
        height: 1,
        flex: 1,
        bgcolor: light ? alpha(CHAMPAGNE, 0.22) : alpha(INK, 0.12),
      }}
    />
    <Box
      sx={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        bgcolor: light ? GOLD : GOLD_DEEP,
      }}
    />
    <Box
      sx={{
        height: 1,
        flex: 1,
        bgcolor: light ? alpha(CHAMPAGNE, 0.22) : alpha(INK, 0.12),
      }}
    />
  </Stack>
);

export default function LandingPage() {
  const [subscribeForm, setSubscribeForm] = useState({
    fullName: "",
    email: "",
  });

  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState({
    type: "",
    text: "",
  });

  const categories = useMemo(
    () => [
      {
        icon: <BrushIcon />,
        title: "Original paintings",
        text: "Curated artist-made works for living rooms, dining rooms, studies and statement walls.",
        chip: "Collector focus",
        tone: "gold",
      },
      {
        icon: <PaletteIcon />,
        title: "Fine prints",
        text: "Elegant printed editions for polished interiors that need immediate visual harmony.",
        chip: "Accessible luxury",
        tone: "turquoise",
      },
      {
        icon: <ViewInArIcon />,
        title: "Sculptural pieces",
        text: "Objects with presence for shelves, alcoves, entrance halls and refined corners.",
        chip: "Dimensional",
        tone: "burgundy",
      },
      {
        icon: <ChairIcon />,
        title: "Decor objects",
        text: "Small finishing pieces chosen for colour, texture, light and interior atmosphere.",
        chip: "Finishing touch",
        tone: "gold",
      },
    ],
    []
  );

  const steps = useMemo(
    () => [
      {
        icon: <CameraAltIcon />,
        title: "Upload the room",
        text: "Begin with the actual interior. The wall, furniture, light and colour story become the brief.",
      },
      {
        icon: <TuneIcon />,
        title: "Refine the intention",
        text: "Choose whether the room needs calm, drama, warmth, elegance, contrast or a centrepiece.",
      },
      {
        icon: <ImageSearchIcon />,
        title: "Receive a shortlist",
        text: "The platform returns art and objects that feel composed for the space, not randomly browsed.",
      },
      {
        icon: <ShoppingBagIcon />,
        title: "Buy or make an offer",
        text: "Purchase available works or send a discreet room offer for selected artist originals.",
      },
    ],
    []
  );

  const buyerCards = useMemo(
    () => [
      {
        icon: <PsychologyIcon />,
        title: "Confidence before purchase",
        text: "The buyer sees why a piece belongs in the room before spending serious money.",
      },
      {
        icon: <StraightenIcon />,
        title: "Interior fit, not endless browsing",
        text: "Colour, scale, mood and visual balance matter more than a generic catalogue page.",
      },
      {
        icon: <HomeRoundedIcon />,
        title: "A room that feels collected",
        text: "The result should feel intentional, private and lived-in, not copied from a showroom.",
      },
    ],
    []
  );

  const artistCards = useMemo(
    () => [
      {
        title: "Prestige positioning",
        text: "Artists are presented inside a room-led buying experience, where the artwork is seen as part of a refined interior decision.",
      },
      {
        title: "Better buyer intent",
        text: "Visitors arrive with a real room and a real need, which gives each artwork a stronger chance of being considered seriously.",
      },
      {
        title: "Less admin friction",
        text: "The upload journey can use painting analysis to help pre-fill title ideas, colour notes, room suggestions and listing copy.",
      },
    ],
    []
  );

  useEffect(() => {
    const canonicalUrl = "https://www.artformyroom.com/";
    const logoUrl = `${window.location.origin}${ASSETS.logo}`;
    const previewImage = `${window.location.origin}${ASSETS.logo}`;

    document.title = "Art For My Room | Luxury Room-Led Art Matching";

    const setMeta = (selector, attrName, attrValue, content) => {
      let el = document.querySelector(`${selector}[${attrName}="${attrValue}"]`);

      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }

      el.setAttribute("content", content);
    };

    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", canonicalUrl);

    setMeta(
      "meta",
      "name",
      "description",
      "Art For My Room is a UK-based luxury art matching website where buyers upload a room photo and discover paintings, prints, sculptures and decorative objects selected for the space."
    );

    setMeta(
      "meta",
      "name",
      "keywords",
      "luxury art marketplace UK, art for my room, room art matcher, match painting with room, buy paintings for living room, interior design art, Knightsbridge art, curated paintings, art concierge"
    );

    setMeta("meta", "name", "robots", "index, follow, max-image-preview:large");
    setMeta("meta", "name", "theme-color", NOIR);

    setMeta(
      "meta",
      "property",
      "og:title",
      "Art For My Room | A private gallery for the room itself"
    );

    setMeta(
      "meta",
      "property",
      "og:description",
      "Upload your room and discover curated paintings, prints, sculptures and decorative objects chosen for the interior."
    );

    setMeta("meta", "property", "og:type", "website");
    setMeta("meta", "property", "og:url", canonicalUrl);
    setMeta("meta", "property", "og:image", previewImage);

    setMeta("meta", "name", "twitter:card", "summary_large_image");
    setMeta(
      "meta",
      "name",
      "twitter:title",
      "Art For My Room | Luxury room-led art matching"
    );
    setMeta(
      "meta",
      "name",
      "twitter:description",
      "A private-gallery style way to match art with your room."
    );
    setMeta("meta", "name", "twitter:image", previewImage);

    const oldJson = document.getElementById("artformyroom-home-jsonld");
    if (oldJson) oldJson.remove();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "artformyroom-home-jsonld";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: SITE_NAME,
          url: canonicalUrl,
          logo: logoUrl,
          email: CONTACT_EMAIL,
          areaServed: "United Kingdom",
          description:
            "Art For My Room is a UK-based room-led art matching platform for paintings, prints, sculptures and decorative objects.",
        },
        {
          "@type": "WebSite",
          name: SITE_NAME,
          url: canonicalUrl,
        },
        {
          "@type": "Service",
          name: "Luxury room-led art matching",
          provider: {
            "@type": "Organization",
            name: SITE_NAME,
            url: canonicalUrl,
          },
          areaServed: "United Kingdom",
          serviceType: "Interior art and decor matching",
          description:
            "Visitors upload a room photo and discover curated paintings, prints, sculptures and decorative objects for the space.",
          url: `${window.location.origin}/artist-room-matcher`,
        },
      ],
    });

    document.head.appendChild(script);

    return () => {
      const injectedJson = document.getElementById("artformyroom-home-jsonld");
      if (injectedJson) injectedJson.remove();
    };
  }, []);

  const handleSubscribeChange = (field) => (event) => {
    setSubscribeForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubscribe = async (event) => {
    event.preventDefault();

    setSubscribeMessage({
      type: "",
      text: "",
    });

    if (!subscribeForm.fullName.trim() || !subscribeForm.email.trim()) {
      setSubscribeMessage({
        type: "error",
        text: "Please add your name and email.",
      });
      return;
    }

    setSubscribeLoading(true);

    try {
      const response = await fetch("/api/opportunity-subscriptions/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName: subscribeForm.fullName.trim(),
          email: subscribeForm.email.trim(),
          organisation: "Art For My Room private preview subscriber",
          role: "Collector / buyer / artist / interior decor subscriber",
          region: "UK",
          industries: ["Luxury Art", "Interior Design", "Creative Technology", "Room Decor"],
          opportunityTypes: [
            "Private Art Drops",
            "Room Matching Updates",
            "Artist Uploads",
            "Interior Styling Notes",
          ],
          notes:
            "Homepage subscriber interested in luxury room-led art matching, curated paintings, prints, sculptures and decorative objects.",
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      let data = { message: "" };

      if (contentType.includes("application/json")) {
        data = await response.json();
      } else {
        data = { message: await response.text() };
      }

      if (!response.ok) {
        throw new Error(data.message || "Subscription could not be completed.");
      }

      setSubscribeMessage({
        type: "success",
        text: "You are on the private preview list. We will send selected Art For My Room updates by email.",
      });

      setSubscribeForm({
        fullName: "",
        email: "",
      });
    } catch (err) {
      setSubscribeMessage({
        type: "error",
        text: err.message || "Subscription could not be completed right now.",
      });
    } finally {
      setSubscribeLoading(false);
    }
  };

  return (
    <Page>
      <GlobalStyles
        styles={{
          html: {
            scrollBehavior: "smooth",
          },
          body: {
            background: IVORY,
          },
          "@media (prefers-reduced-motion: reduce)": {
            "*": {
              animationDuration: "0.001ms !important",
              animationIterationCount: "1 !important",
              transitionDuration: "0.001ms !important",
              scrollBehavior: "auto !important",
            },
          },
        }}
      />

      <Box component="main">
        <Box
          component="section"
          sx={{
            pt: { xs: 3, md: 5 },
            pb: { xs: 6, md: 9 },
          }}
        >
          <Container maxWidth="xl">
            <SalonPanel elevation={0} sx={{ p: { xs: 2.4, md: 4.8 } }}>
              <Grid
                container
                spacing={{ xs: 4, md: 5 }}
                alignItems="center"
                sx={{ position: "relative", zIndex: 1 }}
              >
                <Grid item xs={12} md={5.5}>
                  <Stack spacing={2.5}>
                    <Stack direction="row" spacing={1.4} alignItems="center">
                      <ImageLogo />
                      <Box>
                        <Typography sx={{ fontWeight: 950, lineHeight: 1 }}>
                          {SITE_NAME}
                        </Typography>
                        <Typography
                          sx={{
                            color: alpha(WHITE, 0.62),
                            fontSize: 13,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                          }}
                        >
                          {DOMAIN}
                        </Typography>
                      </Box>
                    </Stack>

                    <Chip
                      icon={<AutoAwesomeIcon />}
                      label="Private room-led art discovery"
                      sx={{
                        alignSelf: "flex-start",
                        bgcolor: alpha(WHITE, 0.10),
                        color: CHAMPAGNE,
                        border: `1px solid ${alpha(CHAMPAGNE, 0.20)}`,
                        fontWeight: 950,
                        "& .MuiChip-icon": { color: GOLD },
                      }}
                    />

                    <Typography
                      component="h1"
                      sx={{
                        fontFamily: DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: { xs: "3.25rem", sm: "4.8rem", md: "6.8rem" },
                        lineHeight: 0.86,
                        letterSpacing: "-0.075em",
                        maxWidth: 760,
                      }}
                    >
                      A private gallery for the room itself.
                    </Typography>

                    <Typography
                      sx={{
                        color: alpha(WHITE, 0.75),
                        lineHeight: 1.82,
                        fontSize: { xs: "1rem", md: "1.12rem" },
                        maxWidth: 650,
                      }}
                    >
                      Art For My Room helps discerning buyers upload an interior and discover
                      paintings, prints, sculptures and decorative objects that feel composed
                      for that exact space. Less catalogue noise, more quiet certainty.
                    </Typography>

                    <LuxuryDivider light />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                      <LuxuryButton
                        component="a" href="/artist-room-matcher"
                        startIcon={<ImageSearchIcon />}
                      >
                        Match My Room
                      </LuxuryButton>

                      <DarkGhostButton
                        component="a" href="/artist-launch"
                        startIcon={<CloudUploadIcon />}
                      >
                        Apply as Artist
                      </DarkGhostButton>
                    </Stack>

                    <Grid container spacing={1.4}>
                      <Grid item xs={4}>
                        <Metric value="Curated" label="not crowded" />
                      </Grid>
                      <Grid item xs={4}>
                        <Metric value="Room" label="first search" />
                      </Grid>
                      <Grid item xs={4}>
                        <Metric value="UK" label="built salon" />
                      </Grid>
                    </Grid>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6.5}>
                  <VideoFrame
                    src={ASSETS.video}
                    label="Signature experience"
                    title="The room becomes the collector's brief."
                    text="The wall, light, colours and furniture guide the art selection before the buyer commits."
                    large
                  />
                </Grid>
              </Grid>
            </SalonPanel>
          </Container>
        </Box>

        <Box component="section" sx={{ pb: { xs: 6, md: 9 } }}>
          <Container maxWidth="xl">
            <Grid container spacing={2.4}>
              <Grid item xs={12} md={6}>
                <VideoFrame
                  src={ASSETS.video1}
                  label="Interior polish"
                  title="For rooms that need presence, not clutter."
                  text="A beautiful room rarely needs more decoration. It needs the one piece that makes the space feel decided."
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <VideoFrame
                  src={ASSETS.video2}
                  label="Personal rooms"
                  title="For spaces with taste, memory and mood."
                  text="Bedrooms, studies and salons can feel collected when the artwork belongs to the interior language."
                />
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 6, md: 9 } }}>
          <Container maxWidth="lg">
            <SectionTitle
              eyebrow="The positioning"
              icon={<AutoAwesomeIcon />}
              title="Not a marketplace. A room-led art salon."
              text="The brand should feel closer to a Knightsbridge gallery than a generic online shop. Buyers arrive with interiors worth refining. Artists join because their work is presented with taste, context and intent."
            />

            <IvoryPanel elevation={0} sx={{ p: { xs: 2.6, md: 4.5 } }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Stack spacing={1.6}>
                    <Typography
                      sx={{
                        fontFamily: MONO_FONT,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: GOLD_DEEP,
                        fontWeight: 950,
                        fontSize: "0.78rem",
                      }}
                    >
                      For buyers
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: { xs: "2rem", md: "2.45rem" },
                        lineHeight: 0.98,
                        letterSpacing: "-0.05em",
                      }}
                    >
                      The only place to start with the room.
                    </Typography>
                    <Typography sx={{ color: MUTED, lineHeight: 1.8 }}>
                      The buyer does not need to scroll through thousands of works. They upload
                      the space and receive art that respects the room's mood, palette and architecture.
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack spacing={1.6}>
                    <Typography
                      sx={{
                        fontFamily: MONO_FONT,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: TURQUOISE_DEEP,
                        fontWeight: 950,
                        fontSize: "0.78rem",
                      }}
                    >
                      For artists
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: { xs: "2rem", md: "2.45rem" },
                        lineHeight: 0.98,
                        letterSpacing: "-0.05em",
                      }}
                    >
                      A more desirable place to be discovered.
                    </Typography>
                    <Typography sx={{ color: MUTED, lineHeight: 1.8 }}>
                      The artwork is not thrown into a crowded shelf. It is matched with rooms,
                      styles and buyers who already want a piece to complete their interior.
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack spacing={1.6}>
                    <Typography
                      sx={{
                        fontFamily: MONO_FONT,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: BURGUNDY,
                        fontWeight: 950,
                        fontSize: "0.78rem",
                      }}
                    >
                      For the brand
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: { xs: "2rem", md: "2.45rem" },
                        lineHeight: 0.98,
                        letterSpacing: "-0.05em",
                      }}
                    >
                      Luxury through confidence and restraint.
                    </Typography>
                    <Typography sx={{ color: MUTED, lineHeight: 1.8 }}>
                      Turquoise brings modern intelligence, gold brings value, burgundy brings
                      warmth and depth. Together they create a private salon feeling.
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </IvoryPanel>
          </Container>
        </Box>

        <Box
          component="section"
          sx={{
            py: { xs: 6, md: 9 },
            bgcolor: NOIR,
            color: WHITE,
            borderTop: `1px solid ${alpha(CHAMPAGNE, 0.12)}`,
            borderBottom: `1px solid ${alpha(CHAMPAGNE, 0.12)}`,
          }}
        >
          <Container maxWidth="lg">
            <SectionTitle
              eyebrow="What we curate"
              icon={<PaletteIcon />}
              title="The pieces that make a room feel collected."
              text="The experience is not about filling a wall. It is about finding the object that gives the room its final sentence."
              light
            />

            <Grid container spacing={2.4}>
              {categories.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item.title}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "100%",
                      p: 2.5,
                      borderRadius: 7,
                      bgcolor: alpha(WHITE, 0.06),
                      color: WHITE,
                      border: `1px solid ${alpha(CHAMPAGNE, 0.16)}`,
                      backdropFilter: "blur(14px)",
                    }}
                  >
                    <Stack spacing={1.8}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <IconMedallion tone="dark">{item.icon}</IconMedallion>
                        <Chip
                          label={item.chip}
                          size="small"
                          sx={{
                            bgcolor: alpha(GOLD, 0.12),
                            color: CHAMPAGNE,
                            border: `1px solid ${alpha(CHAMPAGNE, 0.18)}`,
                            fontWeight: 950,
                          }}
                        />
                      </Stack>

                      <Typography
                        component="h3"
                        sx={{
                          fontFamily: DISPLAY_FONT,
                          fontWeight: 700,
                          fontSize: "1.7rem",
                          letterSpacing: "-0.045em",
                          lineHeight: 1,
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Typography sx={{ color: alpha(WHITE, 0.66), lineHeight: 1.72 }}>
                        {item.text}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 6, md: 9 } }}>
          <Container maxWidth="lg">
            <SectionTitle
              eyebrow="How it works"
              icon={<TuneIcon />}
              title="One room photo. A more intelligent way to choose art."
              text="The journey is intentionally simple. The luxury is not complication. The luxury is removing doubt."
            />

            <Grid container spacing={2.4}>
              {steps.map((item, index) => (
                <Grid item xs={12} sm={6} md={3} key={item.title}>
                  <EditorialCard elevation={0}>
                    <CardContent sx={{ p: 2.7 }}>
                      <Stack spacing={1.75}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <IconMedallion
                            tone={
                              index === 0
                                ? "turquoise"
                                : index === 1
                                  ? "gold"
                                  : index === 2
                                    ? "burgundy"
                                    : "gold"
                            }
                          >
                            {item.icon}
                          </IconMedallion>

                          <Typography
                            sx={{
                              fontFamily: DISPLAY_FONT,
                              fontWeight: 700,
                              color: alpha(INK, 0.22),
                              fontSize: "2rem",
                              lineHeight: 1,
                            }}
                          >
                            0{index + 1}
                          </Typography>
                        </Stack>

                        <Typography
                          component="h3"
                          sx={{
                            fontFamily: DISPLAY_FONT,
                            fontWeight: 700,
                            fontSize: "1.7rem",
                            letterSpacing: "-0.045em",
                            lineHeight: 1,
                          }}
                        >
                          {item.title}
                        </Typography>

                        <Typography sx={{ color: MUTED, lineHeight: 1.75 }}>
                          {item.text}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </EditorialCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 6, md: 9 }, bgcolor: IVORY }}>
          <Container maxWidth="lg">
            <Grid container spacing={3.5} alignItems="center">
              <Grid item xs={12} md={5}>
                <SectionTitle
                  align="left"
                  eyebrow="Buyer confidence"
                  icon={<PsychologyIcon />}
                  title="A luxury buyer does not want to guess."
                  text="High-end purchases need visual confidence. The room-first experience helps the buyer feel that the choice is not random, but composed."
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                  <LuxuryButton
                    component="a" href="/artist-room-matcher"
                    startIcon={<HomeRoundedIcon />}
                  >
                    Start with my room
                  </LuxuryButton>

                  <GhostButton
                    component="a" href="/artist-launch"
                    startIcon={<BrushIcon />}
                  >
                    I am an artist
                  </GhostButton>
                </Stack>
              </Grid>

              <Grid item xs={12} md={7}>
                <Grid container spacing={2.2}>
                  {buyerCards.map((item, index) => (
                    <Grid item xs={12} key={item.title}>
                      <EditorialCard elevation={0}>
                        <CardContent sx={{ p: 2.5 }}>
                          <Stack direction="row" spacing={2} alignItems="flex-start">
                            <IconMedallion
                              tone={index === 0 ? "gold" : index === 1 ? "turquoise" : "burgundy"}
                            >
                              {item.icon}
                            </IconMedallion>

                            <Box>
                              <Typography
                                sx={{
                                  fontFamily: DISPLAY_FONT,
                                  fontWeight: 700,
                                  fontSize: "1.75rem",
                                  letterSpacing: "-0.045em",
                                  lineHeight: 1,
                                }}
                              >
                                {item.title}
                              </Typography>
                              <Typography sx={{ color: MUTED, lineHeight: 1.76, mt: 0.9 }}>
                                {item.text}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </EditorialCard>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 6, md: 9 } }}>
          <Container maxWidth="lg">
            <SalonPanel elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
              <Grid
                container
                spacing={3.5}
                alignItems="center"
                sx={{ position: "relative", zIndex: 1 }}
              >
                <Grid item xs={12} md={6.4}>
                  <SectionTitle
                    align="left"
                    eyebrow="For selected artists"
                    icon={<BrushIcon />}
                    title="Artists should want to be seen here."
                    text="The artist side should feel like an invitation to a better stage. Paintings are not merely uploaded. They are prepared, framed by context and connected to rooms where they can become desirable."
                    light
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                    <LuxuryButton
                      component="a" href="/artist-launch"
                      startIcon={<CloudUploadIcon />}
                    >
                      Apply as Artist
                    </LuxuryButton>

                    <DarkGhostButton component="a" href={`mailto:${CONTACT_EMAIL}`}>
                      Contact the gallery
                    </DarkGhostButton>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={5.6}>
                  <Stack spacing={1.4}>
                    {artistCards.map((item, index) => (
                      <Paper
                        key={item.title}
                        elevation={0}
                        sx={{
                          p: 2.1,
                          borderRadius: 6,
                          bgcolor: alpha(WHITE, 0.08),
                          border: `1px solid ${alpha(CHAMPAGNE, 0.16)}`,
                          color: WHITE,
                        }}
                      >
                        <Stack direction="row" spacing={1.5}>
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              bgcolor:
                                index === 0
                                  ? alpha(GOLD, 0.16)
                                  : index === 1
                                    ? alpha(TURQUOISE, 0.16)
                                    : alpha(BURGUNDY, 0.24),
                              color: CHAMPAGNE,
                              fontFamily: MONO_FONT,
                              fontWeight: 950,
                              flexShrink: 0,
                            }}
                          >
                            0{index + 1}
                          </Box>

                          <Box>
                            <Typography sx={{ fontWeight: 950, color: WHITE }}>
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.45,
                                color: alpha(WHITE, 0.68),
                                lineHeight: 1.68,
                              }}
                            >
                              {item.text}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </SalonPanel>
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 6, md: 9 }, bgcolor: IVORY }}>
          <Container maxWidth="lg">
            <IvoryPanel elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6.7}>
                  <Chip
                    icon={<LocalOfferIcon />}
                    label="Discreet purchase journey"
                    sx={{
                      mb: 2,
                      bgcolor: GOLD_MIST,
                      color: GOLD_DEEP,
                      border: `1px solid ${alpha(GOLD, 0.22)}`,
                      fontWeight: 950,
                      "& .MuiChip-icon": { color: GOLD_DEEP },
                    }}
                  />

                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: DISPLAY_FONT,
                      fontWeight: 700,
                      fontSize: { xs: "2.35rem", md: "4.25rem" },
                      lineHeight: 0.94,
                      letterSpacing: "-0.06em",
                      color: INK,
                    }}
                  >
                    Buy now for simple pieces. Make room offers for originals.
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1.7,
                      color: MUTED,
                      lineHeight: 1.85,
                      maxWidth: 760,
                    }}
                  >
                    Prints and decorative objects can be purchased directly. Original paintings
                    and sculptural works can receive a considered offer, keeping the experience
                    elegant instead of turning the page into a loud auction.
                  </Typography>
                </Grid>

                <Grid item xs={12} md={5.3}>
                  <Stack spacing={1.3}>
                    {[
                      {
                        icon: <PaymentIcon />,
                        title: "Buy Now",
                        text: "For prints, editions and selected decorative objects.",
                      },
                      {
                        icon: <LocalOfferIcon />,
                        title: "Make Room Offer",
                        text: "For original artworks where buyer and artist can agree privately.",
                      },
                      {
                        icon: <CheckCircleIcon />,
                        title: "Clear next step",
                        text: "The buyer is guided without pressure, clutter or confusion.",
                      },
                    ].map((item, index) => (
                      <Paper
                        key={item.title}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 6,
                          bgcolor: WHITE,
                          border: `1px solid ${LINE_LIGHT}`,
                          boxShadow: "0 14px 40px rgba(8, 18, 17, 0.06)",
                        }}
                      >
                        <Stack direction="row" spacing={1.4}>
                          <IconMedallion
                            tone={index === 0 ? "gold" : index === 1 ? "burgundy" : "turquoise"}
                          >
                            {item.icon}
                          </IconMedallion>
                          <Box>
                            <Typography sx={{ fontWeight: 950, color: INK }}>
                              {item.title}
                            </Typography>
                            <Typography sx={{ color: MUTED, lineHeight: 1.68, mt: 0.35 }}>
                              {item.text}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </IvoryPanel>
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 6, md: 9 } }}>
          <Container maxWidth="lg">
            <IvoryPanel elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6.5}>
                  <SectionTitle
                    align="left"
                    eyebrow="Private preview"
                    icon={<EmailIcon />}
                    title="Join the room-led art list."
                    text="Receive selected art drops, artist opportunities, decor notes and platform updates from Art For My Room."
                  />

                  <Box component="form" onSubmit={handleSubscribe}>
                    <Grid container spacing={1.6}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Full name"
                          value={subscribeForm.fullName}
                          onChange={handleSubscribeChange("fullName")}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 4,
                              backgroundColor: WHITE,
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="email"
                          label="Email"
                          value={subscribeForm.email}
                          onChange={handleSubscribeChange("email")}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 4,
                              backgroundColor: WHITE,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    {subscribeMessage.text && (
                      <Alert
                        severity={subscribeMessage.type === "success" ? "success" : "error"}
                        sx={{ mt: 2, borderRadius: 4 }}
                      >
                        {subscribeMessage.text}
                      </Alert>
                    )}

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 2.4 }}>
                      <LuxuryButton
                        type="submit"
                        disabled={subscribeLoading}
                        endIcon={
                          subscribeLoading ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <ArrowForwardIcon />
                          )
                        }
                      >
                        {subscribeLoading ? "Joining..." : "Join private preview"}
                      </LuxuryButton>

                      <GhostButton component="a" href={`mailto:${CONTACT_EMAIL}`}>
                        Email us
                      </GhostButton>
                    </Stack>
                  </Box>
                </Grid>

                <Grid item xs={12} md={5.5}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2.4, md: 3 },
                      borderRadius: 8,
                      bgcolor: INK,
                      color: WHITE,
                      border: `1px solid ${alpha(CHAMPAGNE, 0.18)}`,
                    }}
                  >
                    <Stack spacing={1.45}>
                      {[
                        "Curated drops for rooms with serious design intent",
                        "Artist upload opportunities and listing improvements",
                        "Interior styling notes without noisy newsletter fluff",
                        "Platform updates as the private salon expands",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.2} alignItems="center">
                          <CheckCircleIcon sx={{ color: GOLD }} />
                          <Typography sx={{ color: alpha(WHITE, 0.84), fontWeight: 850 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Divider sx={{ my: 2.4, borderColor: alpha(WHITE, 0.12) }} />

                    <Typography sx={{ color: alpha(WHITE, 0.70), lineHeight: 1.7 }}>
                      Contact:{" "}
                      <Link
                        href={`mailto:${CONTACT_EMAIL}`}
                        sx={{
                          color: CHAMPAGNE,
                          fontWeight: 950,
                          textDecoration: "none",
                        }}
                      >
                        {CONTACT_EMAIL}
                      </Link>
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </IvoryPanel>
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 5, md: 8 } }}>
          <Container maxWidth="lg">
            <IvoryPanel elevation={0} sx={{ p: { xs: 2.6, md: 3.6 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Chip
                    label="Developed with LSBU support"
                    sx={{
                      mb: 1.7,
                      bgcolor: GOLD_MIST,
                      color: GOLD_DEEP,
                      fontWeight: 950,
                    }}
                  />

                  <Typography
                    sx={{
                      fontFamily: DISPLAY_FONT,
                      fontWeight: 700,
                      fontSize: { xs: "2rem", md: "3rem" },
                      letterSpacing: "-0.055em",
                      lineHeight: 0.98,
                      color: INK,
                    }}
                  >
                    Built in the UK, shaped by practical creative technology.
                  </Typography>

                  <Typography sx={{ mt: 1.3, color: MUTED, lineHeight: 1.78 }}>
                    Art For My Room has been developed with support connected to London South Bank
                    University's innovation ecosystem. The aim is to help beautiful artwork find
                    interiors where it can belong, not disappear in a generic marketplace.
                  </Typography>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 6,
                      bgcolor: NOIR,
                      border: `1px solid ${alpha(CHAMPAGNE, 0.16)}`,
                    }}
                  >
                    <Box
                      component="img"
                      src={ASSETS.southBankInnovation}
                      alt="South Bank Innovation powered by LSBU"
                      loading="lazy"
                      decoding="async"
                      sx={{
                        width: "100%",
                        display: "block",
                        objectFit: "contain",
                        borderRadius: 4,
                      }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </IvoryPanel>
          </Container>
        </Box>
      </Box>

      <Box
        component="footer"
        sx={{
          bgcolor: NOIR,
          color: WHITE,
          py: { xs: 6, md: 8 },
          borderTop: `1px solid ${alpha(CHAMPAGNE, 0.16)}`,
        }}
      >
        <Container maxWidth="lg">
          <Stack alignItems="center" spacing={2.2}>
            <ImageLogo sx={{ width: 84, height: 84 }} />

            <Typography
              sx={{
                textAlign: "center",
                fontFamily: DISPLAY_FONT,
                fontWeight: 700,
                fontSize: { xs: "2.5rem", md: "4rem" },
                letterSpacing: "-0.06em",
                lineHeight: 0.94,
              }}
            >
              Art For My Room
            </Typography>

            <Typography
              sx={{
                textAlign: "center",
                color: alpha(WHITE, 0.68),
                lineHeight: 1.78,
                maxWidth: 780,
              }}
            >
              A room-first private gallery for paintings, prints, sculptures and decorative
              objects. Upload the room. Discover the artwork that belongs there.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.2}
              justifyContent="center"
              sx={{ pt: 1 }}
            >
              <LuxuryButton component="a" href="/artist-room-matcher">
                Match My Room
              </LuxuryButton>

              <DarkGhostButton component="a" href="/artist-launch">
                Apply as Artist
              </DarkGhostButton>

              <DarkGhostButton component="a" href={`mailto:${CONTACT_EMAIL}`}>
                Email Us
              </DarkGhostButton>
            </Stack>

            <Divider sx={{ borderColor: alpha(WHITE, 0.12), width: "100%", pt: 2 }} />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <Link
                href={`mailto:${CONTACT_EMAIL}`}
                sx={{
                  color: alpha(WHITE, 0.82),
                  textDecoration: "none",
                  fontWeight: 850,
                }}
              >
                {CONTACT_EMAIL}
              </Link>

              <Link
                component={RouterLink}
                to="/privacy"
                sx={{
                  color: alpha(WHITE, 0.68),
                  textDecoration: "none",
                  fontWeight: 800,
                }}
              >
                Privacy Policy
              </Link>
            </Stack>

            <Typography
              sx={{
                textAlign: "center",
                color: alpha(WHITE, 0.42),
                fontSize: "0.88rem",
                fontFamily: MONO_FONT,
                pt: 1,
              }}
            >
              © {new Date().getFullYear()} {SITE_NAME} · turquoise, gold and burgundy, composed for premium rooms
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Page>
  );
}
